import { LitElement, html, css, styleMap, utils } from '@hh/global.js'

class HHStickyHeader extends LitElement {
  static properties = {
    closeWhenHidden: {
      type: String,
      attribute: 'close-when-hidden',
    },
    _state: {
      type: Number,
      state: true,
    },
    _height: {
      type: Number,
      state: true,
    },
  }

  constructor() {
    super()

    this.headerBounds = {}
    this.currentScrollTop = 0
    this.closeWhenHidden = 'hh-details-modal'

    this._state = -1

    this.doSettings = this.doSettings.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()

    this.mainHeader = document.querySelector('[data-main-header]')
    this.announcementBar = document.querySelector(
      '#shopify-section-announcement-bar'
    )
    this.promoBar = document.querySelector('#shopify-section-promo-bar')

    this.contentHasStickyPrevent = Boolean(document.querySelector('[data-sticky-header-prevent]'))

    if (!this.contentHasStickyPrevent) {
      this.onScrollHandler = this.onScroll.bind(this)
      window.addEventListener('scroll', this.onScrollHandler, { passive: true })
      this.createObserver()
    }

    setTimeout(() => {
      this.doSettings()
    }, 0)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('scroll', this.onScrollHandler)
    cancelAnimationFrame(this.scrollFrame)
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect
      observer.disconnect()
    })

    observer.observe(this)
  }

  doSettings() {
    this.setSpacerHeight()
    this.setDropdownMaxHeight()
    this.setMobileMenuMaxHeight()
  }

  setSpacerHeight() {
    const promoBarEl = this.querySelector('[data-promo-bar]')
    const announcementBarEl = this.querySelector('[data-announcement-bar]')
    const headerEl = this.querySelector('[data-main-header]')
    const spacerEl = this.querySelector('[data-spacer]')

    // Use actual visible heights
    const promoBarHeight = promoBarEl?.offsetHeight || 0
    const announcementBarHeight = announcementBarEl?.offsetHeight || 0
    const headerHeight = headerEl?.offsetHeight || 0

    const totalHeight = promoBarHeight + announcementBarHeight + headerHeight

    // Set CSS vars
    if (this.classList.contains('main-header')) {
      document.documentElement.style.setProperty(
        '--hh-promo-bar-height',
        `${promoBarHeight}px`
      )
      document.documentElement.style.setProperty(
        '--hh-announcement-bar-height',
        `${announcementBarHeight}px`
      )
      document.documentElement.style.setProperty(
        '--hh-header-height',
        `${headerHeight}px`
      )
      document.documentElement.style.setProperty(
        '--hh-total-header-height',
        `${totalHeight}px`
      )
    }

    if (this.contentHasStickyPrevent) {
      return
    }

    this._height = totalHeight

    // Update spacer
    if (spacerEl) {
      spacerEl.style.height = `${totalHeight}px`
    }

    this._state = 0
  }

  setDropdownMaxHeight() {
    const dropdowns = this.querySelectorAll(
      '[data-inline-menu] [data-submenu-child]'
    )
    if (dropdowns) {
      dropdowns.forEach((dropdown) => {
        dropdown.style.maxHeight = `calc( 100vh - ${this._height + 50}px )`
      })
    }
  }

  setMobileMenuMaxHeight() {
    const submenus = this.querySelectorAll('[data-mobile-menu]')
    if (submenus) {
      submenus.forEach((submenu) => {
        submenu.style.top = `-${this._height - 1}px`
      })
    }
  }

  onScroll() {
    // Coalesce scroll events into a single update per frame
    if (this.scrollTicking) return
    this.scrollTicking = true

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollTicking = false
      this.handleScroll()
    })
  }

  handleScroll() {
    if (document.querySelector('.js-header-mega-menu-parent.active-menu')) {
      return
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop

    if (
      scrollTop > this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      this.hide()
    } else if (
      scrollTop < this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      this.reveal()
    } else if (scrollTop <= this.headerBounds.top) {
      this.reset()
    }

    // Toggle once, after the state has settled, so the class never
    // flips on and off within a single scroll tick
    this.classList.toggle('hh-sticky-header--has-scrolled', this._state > 0)

    this.currentScrollTop = scrollTop
  }

  setTopBarsHeight() {
    this.announcementBar ??= document
      .querySelector('[data-announcement-bar]')
      ?.closest('div')

    this.promoBar ??= document.querySelector('[data-promo-bar]')?.closest('div')

    const totalHeight =
      (this.announcementBar?.offsetHeight || 0) +
      (this.promoBar?.offsetHeight || 0)

    document.documentElement.style.setProperty(
      '--top-bars-height',
      `${totalHeight}px`
    )
    return totalHeight
  }

  hide() {
    document.body.classList.remove('sticky-header--reveal')
    if (this._state === 1) return
    this._state = 1

    document.documentElement.style.setProperty('--hh-header-height', `0px`)

    // Hide top bars
    this.announcementBar?.classList.add('hidden-top-bar')
    this.promoBar?.classList.add('hidden-top-bar')

    // Close dropdowns/modals
    utils.all(this.closeWhenHidden, this.header).map((element) => {
      element.setAttribute('open', false)
    })
  }

  reveal() {
    document.body.classList.add('sticky-header--reveal')
    if (this._state === 2) return

    this.setDropdownMaxHeight()
    this.setMobileMenuMaxHeight()
    const totalHeight = this.setTopBarsHeight()

    if (this.id === 'main-header') {
      document.documentElement.style.setProperty(
        '--hh-header-height',
        `${this._height}px`
      )
      if (this.mainHeader) {
        this.mainHeader.style.top = `-${totalHeight}px`
      }
    }

    this.announcementBar?.classList.add('hidden-top-bar')
    this.promoBar?.classList.add('hidden-top-bar')

    const headerHeight = this.mainHeader?.offsetHeight || 0
    this._height = headerHeight

    this._state = 2
  }

  reset() {
    if (this._state === 0) return

    this.doSettings()
    const totalHeight = this.setTopBarsHeight()
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop

    if (scrollTop <= 1) {
      this.announcementBar?.classList.remove('hidden-top-bar')
      this.promoBar?.classList.remove('hidden-top-bar')

      this.mainHeader.style.top = '0px'
    } else {
      this.announcementBar?.classList.add('hidden-top-bar')
      this.promoBar?.classList.add('hidden-top-bar')

      this.mainHeader.style.top = `-${totalHeight}px`
    }

    if (this.id === 'main-header') {
      document.documentElement.style.setProperty(
        '--hh-header-height',
        `${this._height}px`
      )
    }

    this._state = 0
  }

  render() {
    return html`<div
        class="container"
        style="${styleMap({
          position: this._state === -1 ? 'relative' : 'fixed',
          transform: this._state === 1 ? `translateY(-100%)` : 'translateY(0)',
        })}"
      >
        <slot></slot>
      </div>
      <div
        data-spacer
        class="spacer"
        style="${styleMap({ height: `${this._height}px` })}"
      ></div>`
  }

  static styles = css`
    .container {
      left: 0;
      width: 100%;
      z-index: var(--hh-z-index, 100);
      transform: translateY(0);
      transition: transform var(--hh-duration, 500ms) ease;
      background-color: var(--hh-background, transparent);
    }

    .spacer {
      position: relative;
      transition: height 300ms ease;
      will-change: height;
    }
  `
}

customElements.define('hh-sticky-header', HHStickyHeader)
